import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext';

type Props = {
  children: React.ReactNode;
};

export default function AuthRequired({ children }: Props) {
  const { user, isLoading, isCheckingAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  // Check if already on login page
  const isLoginPage = location.pathname === '/login';

  // ENHANCED SOLUTION: Better coordination with AuthContext
  useEffect(() => {
    // Don't do anything while still loading or checking auth
    if (isLoading || isCheckingAuth) {
      return;
    }

    // Mark that we've checked auth at least once
    if (!hasCheckedAuth) {
      setHasCheckedAuth(true);
      return; // Wait for next render cycle
    }

    // Only check for redirect after we've fully loaded and checked auth
    if (!user && !isLoginPage && hasCheckedAuth) {

      // Give the interceptor and AuthContext enough time to:
      // 1. Attempt token refresh (up to 3 retries)
      // 2. Handle network delays
      // 3. Complete user data decryption
      const redirectTimeout = setTimeout(() => {
        // Final check - ensure we're still in the same state
        if (!user && !isLoading && !isCheckingAuth && hasCheckedAuth) {

          // Store current path before redirecting
          if (location.pathname !== '/') {
            // Clear any previous restoration flags
            sessionStorage.removeItem('hasRestoredRoute');
            sessionStorage.removeItem('redirectPath');

            // Store the current route for later restoration
            sessionStorage.setItem(
              'spaRoute',
              location.pathname + location.search + location.hash,
            );
          }

          navigate('/login', { replace: true });
        } else {
        }
      }, 5000); // Increased to 5 seconds for better reliability

      return () => clearTimeout(redirectTimeout);
    } else if (user) {
      // If user is authenticated and we're not on the login page,
      // clear any restoration flags to prevent unwanted redirects
      if (!isLoginPage) {
        sessionStorage.removeItem('hasRestoredRoute');
      }
    }
  }, [
    user,
    navigate,
    isLoginPage,
    isLoading,
    isCheckingAuth,
    location,
    hasCheckedAuth,
  ]);

  // Show loading state while checking authentication
  if (isLoading || isCheckingAuth || !hasCheckedAuth) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600"></div>
          <p className="text-sm text-slate-600">جاري التحقق من الهوية...</p>
        </div>
      </div>
    );
  }

  // If the user is not authenticated, don't render the children
  if (!user) {
    return null;
  }

  return <>{children}</>;
}
